import React, { useState, useEffect, useRef, useCallback } from 'react';
import Pagination from './Pagination';
import TableActionBar from './TableActionBar';
import TableRow from './table/TableRow';
import TableRowFileUpload from './table/TableRowFileUpload';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Help from './Help';
import { useTranslation } from 'react-i18next';
import icons from '../../constants/icons';

export interface SimpleTextFilterProps {
  Key?: string;
  Value?: string;
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  testId?: string;
}

function SimpleTextFilter({ Value, value, onChange, placeholder, testId }: SimpleTextFilterProps) {
  const resolvedValue = Value ?? value ?? '';
  const [filter, setFilter] = useState(resolvedValue);

  // Sync local state when Value/value changes from outside (e.g. URL changed)
  useEffect(() => {
    setFilter(Value ?? value ?? '');
  }, [Value, value]);

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && onChange) {
      onChange(filter);
    }
  };

  return (
    <input
      type="text"
      className={`form-control ${filter ? 'filtered' : ''}`}
      value={filter}
      onChange={(event) => setFilter(event.target.value)}
      onBlur={() => onChange && onChange(filter)}
      onKeyPress={handleKeyPress}
      placeholder={placeholder}
      data-cy={testId}
    />
  );
}

/**
 * @typedef {Object} Option
 * @property {string} value - The value used for the option's value attribute
 * @property {string} label - The text displayed for the option
 */
/**
 * A dropdown component that provides filtering functionality with placeholder support
 * @param {Object} props - Component props
 * @param {string} props.value - Currently selected value
 * @param {Option[]} props.options - Array of options to display in the dropdown
 * @param {function(string): void} props.onChange - Callback fired when selection changes
 * @param {string} [props.placeholder] - Text to display when no option is selected
 * @returns {JSX.Element} A dropdown select element
 */
export interface SimpleDropdownFilterProps {
  value?: string;
  options: { value: string; label: string }[];
  onChange?: (value: string) => void;
  placeholder?: string;
}
function SimpleDropdownFilter({ value, options, onChange, placeholder }: SimpleDropdownFilterProps) {
  const [filter, setFilter] = useState(value ?? '');

  const handleOptionSelect = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const v = event.target.value;
    setFilter(v);
    onChange?.(v);
  };

  return (
    <select
      className={`form-control ${filter ? 'filtered' : ''}`}
      value={filter}
      onChange={handleOptionSelect}
      onBlur={() => onChange?.(filter)}
      data-placeholder={placeholder}
    >
      <option value="">{placeholder ?? ''}</option>
      {options.map((option: { value: string; label: string }) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  );
}

function HeaderColumn({
  sortedField,
  setSortedField,
  col,
  isLoading,
  handleResizeMouseDown,
  handleResizeDoubleClick,
  resizeKeyRef,
}: {
  sortedField?: { key: string; direction: string };
  setSortedField?: (e: { key: string; direction: string }) => void;
  col: TableColumn;
  isLoading?: boolean;
  handleResizeMouseDown?: (e: React.MouseEvent, columnKey: string) => void;
  handleResizeDoubleClick?: (e: React.MouseEvent, columnKey: string) => void;
  resizeKeyRef: React.RefObject<string | null>;
}) {
  let getTHSortClassNames = (col: TableColumn) => {
    let resizable = col.Resize !== false ? 'resizable' : '';
    if ((col.Sort ?? true) && sortedField !== undefined) {
      if (col.Key === sortedField.key) {
        return `sortable ${sortedField.direction === 'asc' ? 'sorted_asc' : 'sorted_desc'
          } ${col.className || ''} ${resizable} ${isLoading ? 'disabled' : ''}`;
      }
      return `sortable ${col.className || ''} ${resizable} ${isLoading ? 'disabled' : ''
        }`;
    }
    return `${col.className || ''} ${resizable}`;
  };

  let getTHSortIcon = (col: TableColumn) => {
    if ((col.Sort ?? true) && sortedField !== undefined) {
      if (col.Key === sortedField.key) {
        return sortedField.direction === 'asc' ? (
          <FontAwesomeIcon icon={icons.SORT_ASC} />
        ) : (
          <FontAwesomeIcon icon={icons.SORT_DESC} />
        );
      }
      return <FontAwesomeIcon icon={icons.SORT_ASC} />;
    }
    return <></>;
  };

  let handleSort = () => {
    if (resizeKeyRef.current === col.Key) {
      return;
    }

    if (setSortedField && !isLoading) {
      setSortedField({
        key: col.Key,
        direction:
          sortedField !== undefined &&
            sortedField.key === col.Key &&
            sortedField.direction === 'asc'
            ? 'desc'
            : 'asc',
      });
    }
  };

  // We are ignoring the % widths in the column definitions, this is to allow the resize logic to work
  // better on tables with a lot of columns. 24/01/2025
  return (
    <th
      className={getTHSortClassNames(col)}
      onMouseDown={handleSort}
      style={{
        width: col.width?.includes('px') ? col.width : 'auto',
        position: 'relative',
        minWidth: col.width?.includes('px') ? col.width : undefined,
        maxWidth: col.width?.includes('px') ? col.width : undefined,
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
        }}
      >
        <div className="w-100 flex align-items-center">
          <div className={`${col.className || ''} w-100 user-select-none`}>
            {col.Label}
          </div>
          {col.helpContent && (
            <Help helpContent={col.helpContent} className={'ms-2'} />
          )}
        </div>
        <div className="sort-icon">{getTHSortIcon(col)}</div>
      </div>
      {col.Resize !== false && (
        <div
          className={`resize-handle ${resizeKeyRef.current === col.Key ? 'active' : ''
            }`}
          onMouseDown={(e) => handleResizeMouseDown?.(e, col.Key)}
          onDoubleClick={(e) => handleResizeDoubleClick?.(e, col.Key)}
        />
      )}
    </th>
  );
}

function FilterColumn({
  filterChange,
  col,
  filterValue,
}: {
  filterChange?: (key: string, value: string) => void;
  col: TableColumn;
  filterValue?: string;
}) {
  const colWithValue = filterValue !== undefined ? { ...col, filterValue } : col;

  const getFilter = (c: TableColumn) =>
    c.Filter !== undefined &&
    (c.Filter.Render !== undefined ? (
      (() => {
        const Render = c.Filter!.Render;
        return Render
          ? (Render as (column: TableColumn) => React.ReactNode)(colWithValue) as React.ReactNode
          : null;
      })()
    ) : (
      <SimpleTextFilter
        testId={`${c.Key}-filter-input`}
        value={(colWithValue as TableColumn & { filterValue?: string }).filterValue ?? c.Filter!.Value}
        onChange={(value) => filterChange?.(c.Key, value)}
      />
    ));

  return <th className={col.className}>{getFilter(col)}</th>;
}

/**
 * Resolves a raw filter value (e.g. from URL) to the matching option from a list, case-insensitive,
 * so the select displays the correct casing (e.g. "pending keys" → "Pending keys").
 */
export function resolveSelectValue(value: string, options: string[]): string {
  if (!value) return '';
  const found = options.find((opt) => opt.toLowerCase() === value.toLowerCase());
  return found ?? value;
}

export interface TableColumn {
  Key: string;
  Label: any;
  Render?: (item: any) => React.ReactNode;
  Filter?: {
    Value?: string;
    Render?: (col: any) => React.ReactNode;
  };
  Sort?: boolean;
  Resize?: boolean;
  className?: string;
  width?: string;
  helpContent?: React.ReactNode;
  /** Current filter value from URL/hook; set by Table so filter inputs show URL params on load. */
  filterValue?: string;
}

export interface TableSettings {
  columns?: string[];
  sortedField?: { key: string; direction: string };
  pageNumber?: number;
  pageSize?: number;
  widths?: Record<string, string>[];
}

/**
 * Returns table columns in the order of visibleColumn keys, excluding any keys
 * that don't exist in columnsConfig.
 */
export function getVisibleColumns(
  visibleColumns: string[],
  columnsConfig: TableColumn[]
): TableColumn[] {
  return visibleColumns
    .map((x) => columnsConfig.find((y) => y.Key === x))
    .filter((x): x is TableColumn => x != null);
}

export interface TableProps {
  columns: TableColumn[];
  allColumns: string[];
  defaultVisibleColumns?: string[];
  rows: unknown[];
  totalRowCount: number;
  filteredRowCount: number;
  tableSettings: unknown;
  setTableSettings: (s: any) => void;
  filterChange: (key: string, value: string) => void;
  className?: string;
  isLoading?: boolean;
  noDataComponent?: React.ReactNode;
  trStyle?: unknown;
  exportData?: (filters: unknown, scopes: unknown) => void | Promise<void>;
  setBulkActionsSlideoutOpen?: unknown;
  itemsChecked?: unknown[];
  bulkActionTitle?: string;
  tableActionBarButtons?: React.ReactNode;
  suffixColumns?: unknown[];
  filters?: unknown;
  scopes?: unknown;
  setBreadcrumb?: (b: unknown) => void;
  hideScopeTags?: boolean;
  shouldRowUpload?: boolean;
  handleRowFileUpload?: unknown;
  testId?: string;
}

function Table({
  columns,
  allColumns,
  defaultVisibleColumns,
  rows,
  totalRowCount,
  filteredRowCount,
  tableSettings,
  setTableSettings,
  className,
  isLoading,
  noDataComponent,
  filterChange,
  trStyle,
  exportData,
  setBulkActionsSlideoutOpen,
  itemsChecked,
  bulkActionTitle,
  tableActionBarButtons,
  suffixColumns,
  filters,
  scopes,
  setBreadcrumb,
  hideScopeTags,
  shouldRowUpload,
  handleRowFileUpload,
  testId,
}: TableProps) {
  const { t } = useTranslation();
  const settings = tableSettings as TableSettings;
  const setSortedField = (e: { key: string; direction: string }) =>
    setTableSettings({ ...(tableSettings as object), sortedField: e });
  const setPageNumber = (e: number) =>
    setTableSettings({ ...(tableSettings as object), pageNumber: e });
  const setPageSize = (e: number) => {
    setTableSettings({ ...(tableSettings as object), pageSize: e, pageNumber: 0 });
  };
  const setVisibleColumns = (cols: string[]) => {
    setTableSettings({ ...(tableSettings as object), columns: cols });
  };

  const [isResizing, setIsResizing] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const resizingRef = useRef<string | null>(null);
  const startXRef = useRef<number | null>(null);
  const startWidthRef = useRef<number | null>(null);
  const [internalColumns, setInternalColumns] = useState<TableColumn[]>(
    columns.map((x: TableColumn) => ({
      ...x,
      width: settings.widths?.find((y: Record<string, string>) => y[x.Key])?.[x.Key] || x.width,
    }))
  );

  const debouncedSetTableWidths = useCallback(
    (newCols: TableColumn[]) => {
      if (debounceRef.current != null) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        setTableSettings({
          ...(tableSettings as object),
          widths: newCols
            .filter((x: TableColumn) => x.width?.includes('px'))
            .map((x: TableColumn) => ({ [x.Key]: x.width })),
        });
      }, 250);
    },
    [setTableSettings, tableSettings]
  );

  useEffect(() => {
    setInternalColumns(
      columns.map((x: TableColumn) => ({
        ...x,
        width: settings.widths?.find((y: Record<string, string>) => y[x.Key])?.[x.Key] || x.width,
      }))
    );
  }, [tableSettings, columns]);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!resizingRef.current || startXRef.current == null || startWidthRef.current == null) {
        return;
      }
      const diffX = e.pageX - startXRef.current;
      const newWidth = Math.max(50, startWidthRef.current + diffX);

      setInternalColumns((prev: TableColumn[]) => {
        const newCols = prev.map((col: TableColumn) => {
          if (col.Key === resizingRef.current) {
            return {
              ...col,
              width: `${newWidth - (col.Sort !== false ? 18 : 0)}px`,
            };
          }
          return col;
        });

        debouncedSetTableWidths(newCols);

        return newCols;
      });
    };

    const handleMouseUp = () => {
      resizingRef.current = null;
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove as EventListener);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove as EventListener);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [debouncedSetTableWidths, isResizing]);

  const handleResizeMouseDown = useCallback((e: React.MouseEvent, columnKey: string) => {
    e.preventDefault();
    resizingRef.current = columnKey;
    startXRef.current = e.pageX;
    startWidthRef.current = (e.target as HTMLElement).parentElement?.offsetWidth ?? 0;
    setIsResizing(true);
  }, []);

  const handleResizeDoubleClick = (e: React.MouseEvent, columnKey: string) => {
    e.preventDefault();
    setInternalColumns((prev: TableColumn[]) => {
      const newCols = prev.map((col: TableColumn) => {
        if (col.Key === columnKey) {
          const originalWidth = columns.find(
            (x: TableColumn) => x.Key === col.Key
          )?.width;

          if (originalWidth) {
            return {
              ...col,
              width: originalWidth,
            };
          } else {
            delete col.width;
          }
        }
        return col;
      });

      debouncedSetTableWidths(newCols);
      return newCols;
    });
  };

  let columnsWithSuffix: TableColumn[] = suffixColumns
    ? [...internalColumns, ...(suffixColumns as TableColumn[])]
    : internalColumns;

  const showNoDataComponent =
    !isLoading && noDataComponent !== undefined && totalRowCount === 0;

  useEffect(() => {
    if (Array.isArray(scopes) && setBreadcrumb) {
      setBreadcrumb((prevState: { isScope?: boolean }[]) => [
        ...prevState.filter((x: { isScope?: boolean }) => !x.isScope),
        ...(scopes as { name: string }[]).map((scope: { name: string }) => ({ label: scope.name, isScope: true })),
      ]);
    }
  }, [scopes, setBreadcrumb]);

  return showNoDataComponent ? (
    noDataComponent
  ) : (
    <>
      <TableActionBar
        exportData={exportData}
        setBulkActionsSlideoutOpen={setBulkActionsSlideoutOpen}
        itemsChecked={itemsChecked}
        bulkActionTitle={bulkActionTitle}
        columns={internalColumns.map((x) => x.Key)}
        allColumns={allColumns}
        defaultVisibleColumns={defaultVisibleColumns || allColumns}
        setVisibleColumns={setVisibleColumns}
        filters={filters}
        scopes={scopes}
        hideScopeTags={hideScopeTags}
        tableSettings={tableSettings}
        setTableSettings={setTableSettings}
      >
        {tableActionBarButtons}
      </TableActionBar>
      <div className="table-wrapper">
        <div className={`loadingframe ${isLoading ? 'loading' : ''}`}>
          <div className="loadingbar"></div>
        </div>
        <table
          data-cy={testId}
          className={`table dataTable width-100 ${className || ''} ${resizingRef.current ? 'user-select-none' : ''
            }`}
        >
          <thead>
            <tr>
              {columnsWithSuffix.map((col) => {
                return col.Sort === false ? (
                  <HeaderColumn
                    key={col.Key}
                    col={col}
                    isLoading={isLoading}
                    handleResizeMouseDown={handleResizeMouseDown}
                    handleResizeDoubleClick={handleResizeDoubleClick}
                    resizeKeyRef={resizingRef}
                  />
                ) : (
                  <HeaderColumn
                    key={col.Key}
                    col={col}
                    sortedField={settings.sortedField}
                    setSortedField={setSortedField}
                    isLoading={isLoading}
                    handleResizeMouseDown={handleResizeMouseDown}
                    handleResizeDoubleClick={handleResizeDoubleClick}
                    resizeKeyRef={resizingRef}
                  />
                );
              })}
            </tr>

            {columnsWithSuffix.some((x) => x.Filter !== undefined) && (
              <tr>
                {columnsWithSuffix.map((col) => {
                  const filterValue =
                    filters && typeof filters === 'object' && !Array.isArray(filters)
                      ? (filters as Record<string, string>)[col.Key]
                      : undefined;
                  return (
                    <FilterColumn
                      key={col.Key}
                      col={col}
                      filterChange={filterChange}
                      filterValue={filterValue}
                    />
                  );
                })}
              </tr>
            )}
          </thead>
          <tbody>
            {rows.map((row: unknown) => {
              const r = row as Record<string, unknown>;
              const rowKey = (r.Key ?? r.key) as React.Key;
              const shouldUpload = typeof shouldRowUpload === 'function' && (shouldRowUpload as (r: unknown) => boolean)(row);
              return shouldUpload ? (
                <TableRowFileUpload
                  key={rowKey}
                  row={row}
                  rows={rows}
                  totalRowCount={totalRowCount}
                  sortedField={settings.sortedField}
                  trStyle={trStyle}
                  columnsWithSuffix={columnsWithSuffix}
                  handleRowFileUpload={handleRowFileUpload}
                  resizingRef={resizingRef}
                />
              ) : (
                <TableRow
                  key={rowKey}
                  row={row}
                  rows={rows}
                  totalRowCount={totalRowCount}
                  sortedField={settings.sortedField}
                  trStyle={trStyle}
                  columnsWithSuffix={columnsWithSuffix}
                  resizingRef={resizingRef}
                />
              );
            })}
          </tbody>
        </table>
        {rows && rows.length === 0 && !isLoading ? (
          <div className="d-flex justify-content-center align-items-center">
            <FontAwesomeIcon
              icon={icons.NOTHING_TO_SHOW}
              // No constant for this
              size="3x"
              className="me-3 c-dark-yellow"
            />{' '}
            {t('Nothing to show')}
          </div>
        ) : (
          ''
        )}
        <Pagination
          totalRowCount={totalRowCount}
          filteredRowCount={filteredRowCount}
          pageNumber={settings.pageNumber}
          setPageNumber={setPageNumber}
          pageSize={settings.pageSize}
          setPageSize={setPageSize}
          isLoading={isLoading}
        />
      </div>
    </>
  );
}
export { Table, SimpleTextFilter, SimpleDropdownFilter };
